import { useEffect, useState, useCallback } from 'react';
import { Paper, Stack, Typography, Button, Chip, Alert, Card, CardContent, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Grid, Switch, FormControlLabel, MenuItem } from '@mui/material';
import { Add, Edit, Delete, ReceiptLong } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import axiosInstance from '../utils/axiosInstance';
import ClientTabs from '../components/ClientTabs';
import { useSnackbar } from '../contexts/SnackbarProvider';
import TableView from '../components/TableView';
import { formatPaymentMonth } from '../utils/format';
const defaultValues = {
    rule_name: '',
    closing_day: 31,
    payment_month_offset: 1,
    payment_day: 31,
    billing_basis: 'shipped_at',
    invoice_note: '',
    is_active: true
};
const formatDay = (day) => `${Number(day)}日`;

const getBillingBasisLabel = (basis) => {
    switch (basis) {
        case 'ordered_at':
            return '受注日';
        case 'shipped_at':
            return '出荷日';
        case 'delivered_at':
            return '納品日';
        default:
            return '-';
    }
};
/*const formatPaymentMonth = (offset) => {
    switch (Number(offset)) {
        case 0:
            return '当月';
        case 1:
            return '翌月';
        case 2:
            return '翌々月';
        default:
            return `${offset}ヶ月後`;
    }
};*/
const dayOptions = Array.from({ length: 31 }, (_, i) => i + 1);

const ClientBillingPage = () => {
    const { id } = useParams();
    const { showSnackbar } = useSnackbar();

    const [client, setClient] = useState(null);
    const [rules, setRules] = useState([]);
    const [open, setOpen] = useState(false);
    const [editingRule, setEditingRule] = useState(null);

    const { control, handleSubmit, reset } = useForm({
        defaultValues
    });

    const fetchData = useCallback(async () => {
        try {
            const res = await axiosInstance.get(`/client/${id}/billing-rules`);

            setClient(res.data.client || null);
            setRules(Array.isArray(res.data.items) ? res.data.items : []);

        } catch (err) {
            console.error(err);

            showSnackbar(
                err?.response?.data?.message || '請求設定の取得に失敗しました',
                'error'
            );
        }
    }, [id, showSnackbar]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleOpen = (rule = null) => {
        setEditingRule(rule);

        reset({
            rule_name: rule?.rule_name || '',
            closing_day: Number(rule?.closing_day || 31),
            payment_month_offset: Number(rule?.payment_month_offset || 1),
            payment_day: Number(rule?.payment_day || 31),
            billing_basis: rule?.billing_basis || 'shipped_at',
            invoice_note: rule?.invoice_note || '',
            is_active: rule?.is_active !== false
        });

        setOpen(true);
    };

    const handleClose = () => {
        setEditingRule(null);
        setOpen(false);
        reset(defaultValues);
    };

    const onSubmit = async (data) => {
        try {

            const payload = {
                ...data,
                closing_day: Number(data.closing_day),
                payment_month_offset: Number(data.payment_month_offset),
                payment_day: Number(data.payment_day),
                is_active: Boolean(data.is_active)
            };

            if (editingRule) {

                await axiosInstance.put(
                    `/client/${id}/billing-rules/${editingRule.billing_rule_id}`,
                    payload
                );

                showSnackbar('請求サイクルを更新しました', 'success');

            } else {

                await axiosInstance.post(
                    `/client/${id}/billing-rules`,
                    payload
                );

                showSnackbar('請求サイクルを追加しました', 'success');
            }

            handleClose();
            fetchData();

        } catch (err) {
            console.error(err);

            showSnackbar(
                err?.response?.data?.message || '保存に失敗しました',
                'error'
            );
        }
    };

    const columns = [
        {
            label: 'ルール名',
            accessor: 'rule_name',
            width: 250,
            render: (item) => (
                <Typography>{item.rule_name}</Typography>
            )
        },
        {
            label: '請求基準',
            accessor: 'billing_basis',
            width: 140,
            render: (item) => (
                <>{getBillingBasisLabel(item.billing_basis)}基準</>
            )
        },
        {
            label: '締め日',
            accessor: 'closing_day',
            width: 120,
            render: (item) => (
                <>{formatDay(item.closing_day)}締め</>
            )
        },
        {
            label: '支払日',
            accessor: 'payment_day',
            width: 180,
            render: (item) => (
                <>
                    {formatPaymentMonth(item.payment_month_offset)}
                    {formatDay(item.payment_day)}支払い
                </>
            )
        },
        {
            accessor: 'is_active',
            width: 120,
            render: (item) => (
                <Chip
                    label={item.is_active ? '有効' : '無効'}
                    color={item.is_active ? 'success' : 'default'}
                    size="small"
                />
            )
        },
        {
            label: '',
            accessor: 'actions',
            width: 80,
            render: (item) => (
                <IconButton onClick={() => handleOpen(item)}>
                    <Edit />
                </IconButton>
            )
        }
    ];
    return (
        <>
            <Typography variant="h1" component="h1">
                <Chip label="取引先情報" sx={{ mb: 2, fontWeight: 'normal' }} />
                <br />
                {client?.client_name || ''}
            </Typography>

            <ClientTabs clientId={id} />

            <Paper variant="section">

                <Stack spacing={2}>

                    <Stack direction="row" justifyContent="space-between" alignItems="center">

                        <Stack direction="row" spacing={1} alignItems="center">
                            <ReceiptLong color="primary" />
                            <Typography variant="h4">請求サイクル</Typography>
                        </Stack>

                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => handleOpen()}
                        >
                            サイクル追加
                        </Button>

                    </Stack>

                    <Alert severity="info">
                        サイクル未設定の注文は都度請求として扱われます
                    </Alert>
                    {rules.length === 0 && (
                        <Alert severity="warning">
                            請求サイクルが登録されていません
                        </Alert>
                    )}
                    <TableView
                        dataset={rules}
                        columns={columns}
                        keyColumn="billing_rule_id"
                    />

                </Stack>

            </Paper>

            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">

                <DialogTitle>
                    {editingRule ? '請求サイクル編集' : '請求サイクル追加'}
                </DialogTitle>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogContent>
                        <Alert severity='info' sx={{ mb: 1 }}>末日は「31日」で設定してください。</Alert>
                        <Stack spacing={2} >
                            <Alert severity='info'>都度請求の場合は設定不要です。</Alert>
                            <Controller
                                name="rule_name"
                                control={control}
                                rules={{
                                    required: 'ルール名は必須です',
                                    maxLength: {
                                        value: 100,
                                        message: '100文字以内で入力してください',
                                    },
                                }}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        label="ルール名"
                                        inputProps={{
                                            maxLength: 100
                                        }}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}
                            />
                            <Stack spacing={2} direction='row' sx={{ mt: 1 }}>
                                <Controller
                                    name="billing_basis"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            select
                                            fullWidth
                                            label="請求基準"
                                        >
                                            <MenuItem value="ordered_at">受注日</MenuItem>
                                            <MenuItem value="shipped_at">出荷日</MenuItem>
                                            <MenuItem value="delivered_at">納品日</MenuItem>
                                        </TextField>
                                    )}
                                />
                                <Controller
                                    name="closing_day"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            select
                                            fullWidth
                                            label="締め日"
                                        >
                                            {dayOptions.map(day => (
                                                <MenuItem key={day} value={day}>
                                                    {day}日
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    )}
                                />
                                <Controller
                                    name="payment_month_offset"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            select
                                            fullWidth
                                            label="支払月"
                                        >
                                            <MenuItem value={0}>当月</MenuItem>
                                            <MenuItem value={1}>翌月</MenuItem>
                                            <MenuItem value={2}>翌々月</MenuItem>
                                            <MenuItem value={3}>3ヶ月後</MenuItem>
                                        </TextField>
                                    )}
                                />
                                <Controller
                                    name="payment_day"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            select
                                            fullWidth
                                            label="支払日"
                                        >
                                            {dayOptions.map(day => (
                                                <MenuItem key={day} value={day}>
                                                    {day}日
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    )}
                                />
                            </Stack>
                            <Controller
                                name="invoice_note"
                                control={control}
                                rules={{
                                    maxLength: {
                                        value: 2000,
                                        message: '2000文字以内で入力してください'
                                    }
                                }}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        multiline
                                        minRows={3}
                                        label="請求書備考"
                                        inputProps={{
                                            maxLength: 2000
                                        }}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}
                            />
                            <Controller
                                name="is_active"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        label="有効"
                                        control={
                                            <Switch
                                                checked={field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                            />
                                        }
                                    />
                                )}
                            />

                        </Stack>

                    </DialogContent>

                    <DialogActions>

                        <Button onClick={handleClose}>
                            キャンセル
                        </Button>

                        <Button type="submit" variant="contained">
                            保存
                        </Button>

                    </DialogActions>

                </form>

            </Dialog>

        </>
    );
};

export default ClientBillingPage;